import { Icon, spacing } from '@scality/core-ui';
import { Button } from '@scality/core-ui/dist/next';
import type React from 'react';
import styled from 'styled-components';
import { useIsDashboardMode } from '../../hooks/useIsDashboardMode';
import { usePanelStore } from '../hooks/usePanelStore';
import { InPanelProvider } from '../InPanelContext';
import { MainPanelBody, MainPanelHeader, MainPanelRoot } from './dualPanel.styles';
import { PanelItemCard } from './PanelItemCard';
import { PanelTabs } from './PanelTabs';
import { PanelTitle } from './PanelTitle';

const ClosePanelButton = styled(Button)`
  margin-bottom: ${spacing.f8};
  margin-top: ${spacing.f8};
`;

export const MainPanel: React.FC = () => {
  const { panels, activePanelId, setActivePanel, renamePanel, setCollapsed, closePanel } = usePanelStore();
  const isDashboardMode = useIsDashboardMode();

  if (panels.length === 0) {
    return null;
  }

  const activePanel = panels.find((panel) => panel.id === activePanelId) ?? panels[0];

  // One panel is named rather than offered as a choice; tabs come back at two. A lone dashboard
  // gets neither: it already names itself in its own header.
  const isSinglePanel = panels.length === 1;
  const isLoneDashboard = isSinglePanel && activePanel.source === 'dashboard';

  return (
    <MainPanelRoot>
      {!isLoneDashboard && (
        <MainPanelHeader>
          {isSinglePanel ? (
            <PanelTitle panel={activePanel} onRename={renamePanel} />
          ) : (
            <PanelTabs
              panels={panels}
              activePanelId={activePanel.id}
              onSelect={setActivePanel}
              onRename={renamePanel}
              // In dashboard mode the panel can't collapse, so widget tabs get a close button instead.
              onClose={isDashboardMode ? closePanel : undefined}
            />
          )}
          {!isDashboardMode && (
            <ClosePanelButton
              variant="outline"
              icon={<Icon name="Chevron-left" />}
              onClick={() => setCollapsed(true)}
              tooltip={{ overlay: 'Collapse panel' }}
            />
          )}
        </MainPanelHeader>
      )}
      <InPanelProvider value={true}>
        {activePanel.source === 'dashboard' ? (
          activePanel.content
        ) : (
          <MainPanelBody>
            {activePanel.items.map((item) => (
              <PanelItemCard key={item.id} item={item} />
            ))}
          </MainPanelBody>
        )}
      </InPanelProvider>
    </MainPanelRoot>
  );
};
